import { useCallback, useEffect, useState } from 'react';

export const useGridRemainingHeight = ({
  containerRef,
  gridDataLength,
  customGridRoot,
  getRemainingHeight,
}) => {
  const [remainingGridHeight, setRemainingGridHeight] = useState(0);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

  const measureGridHeight = useCallback(() => {
    const container = containerRef?.current;
    if (!container) return;

    const gridRoot =
      (typeof getGridRoot === 'function' && customGridRoot) ||
      container.querySelector(':scope > .k-grid') ||
      container.querySelector('.k-grid') ||
      null;

    const gridContent = gridRoot?.querySelector('.k-grid-content');
    const gridHeader = gridRoot?.querySelector('.k-grid-header');
    const gridTableBody = gridRoot?.querySelector('.k-table-tbody');
    const gridFooter = gridRoot?.querySelector('.k-grid-footer');

    if (!gridContent || !gridHeader || !gridTableBody) return;

    const fillerRow = gridTableBody.querySelector('.trl-grid-filler-row');
    const fillerHeight = fillerRow?.offsetHeight || 0;
    const rowsHeight = Math.max(
      (gridTableBody.scrollHeight || 0) - fillerHeight,
      0,
    );

    const remainingHeight = getRemainingHeight({
      container,
      gridContent,
      rowsHeight,
      gridFooter,
    });

    const nextHasHorizontalScroll =
      gridContent.offsetWidth < gridContent.scrollWidth;
    const nextHasVerticalScroll =
      gridContent.offsetHeight < gridContent.scrollHeight;
    const nextRemainingHeight =
      remainingHeight - (nextHasHorizontalScroll ? 6 + 4 : 0); // scroll bar + space between table and scroll bar

    setHasHorizontalScroll(nextHasHorizontalScroll);
    setHasVerticalScroll(nextHasVerticalScroll);
    setRemainingGridHeight(nextRemainingHeight > 0 ? nextRemainingHeight : 0);
  }, [containerRef, customGridRoot, getRemainingHeight]);

  useEffect(() => {
    const rafId = requestAnimationFrame(measureGridHeight);
    window.addEventListener('resize', measureGridHeight);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measureGridHeight);
    };
  }, [gridDataLength, measureGridHeight]);

  return {
    remainingGridHeight,
    hasHorizontalScroll,
    hasVerticalScroll,
    measureGridHeight,
  };
};
